"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import useSWR from "swr";
import type { SessionType } from "@/util/types/globalTypes";
import { getApiBaseUrl } from "@/lib/auth-client";
import {
  isValidCompiledSession,
  transformCompiledSession,
} from "@/lib/auth-session";

type SessionStatus = "loading" | "authenticated" | "unauthenticated";

interface WebSessionContextValue {
  data: SessionType | null;
  status: SessionStatus;
  update: () => Promise<void>;
}

const WebSessionContext = createContext<WebSessionContextValue>({
  data: null,
  status: "loading",
  update: async () => {},
});

export const SESSION_SWR_KEY = "web-session";

class UnauthenticatedError extends Error {
  constructor() {
    super("unauthenticated");
    this.name = "UnauthenticatedError";
  }
}

async function fetchSession(): Promise<SessionType | null> {
  const baseUrl = getApiBaseUrl();
  if (!baseUrl) {
    throw new Error("No API base URL available");
  }

  const response = await fetch(`${baseUrl}/auth/web/compile-session`, {
    method: "GET",
    credentials: "include",
  });

  // 401/403 are the only definitive "logged out" signals. Any other failure
  // (network, 5xx) should surface as an error so we don't log users out on a
  // transient blip.
  if (response.status === 401 || response.status === 403) {
    throw new UnauthenticatedError();
  }
  if (!response.ok) {
    throw new Error(`compile-session failed with status ${response.status}`);
  }

  const result = await response.json();
  if (!result?.session || !isValidCompiledSession(result.session)) {
    throw new UnauthenticatedError();
  }

  return transformCompiledSession(result.session);
}

export function WebSessionProvider({
  children,
  initialSession = null,
}: {
  children: ReactNode;
  initialSession?: SessionType | null;
}) {
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    SessionType | null,
    Error
  >(SESSION_SWR_KEY, fetchSession, {
    fallbackData: initialSession ?? undefined,
    revalidateOnFocus: false,
    dedupingInterval: 60_000,
    shouldRetryOnError: (err) => !(err instanceof UnauthenticatedError),
    errorRetryCount: 1,
  });

  const update = useCallback(async () => {
    await mutate();
  }, [mutate]);

  const value = useMemo<WebSessionContextValue>(() => {
    let status: SessionStatus;
    if (data) {
      status = "authenticated";
    } else if (error instanceof UnauthenticatedError) {
      status = "unauthenticated";
    } else if (isLoading || isValidating) {
      status = "loading";
    } else if (error) {
      // Network/5xx error: stay in loading rather than logging the user out.
      status = "loading";
    } else {
      status = "unauthenticated";
    }

    return {
      data: data ?? null,
      status,
      update,
    };
  }, [data, error, isLoading, isValidating, update]);

  return (
    <WebSessionContext.Provider value={value}>
      {children}
    </WebSessionContext.Provider>
  );
}

export function useWebSession() {
  return useContext(WebSessionContext);
}
